import { Button, Loading, Table } from '@/components';
import {
  FALLBACK_TEXT,
  TABLE_BUTTON_WIDTH,
  TABLE_DATETIME_WIDTH,
  dateFormat,
  datetimeFormat,
} from '@/utils';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useBatches } from '../_hooks/batch';
import { useBatchSearchQueryStates } from '../_hooks/search-query-states';
import { BatchTypeLabelModifier } from '../_models/batch';
import {
  convertBatchStatus,
  convertBatchTarget,
} from '../_utils/batch-converter';

export const BatchesTable: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchQueryStates, setSearchQueryStates] = useBatchSearchQueryStates();

  const { batchType, createdAtFrom, createdAtTo, page } = searchQueryStates;

  const batches = useBatches({
    params: {
      batchType,
      createdAtFrom,
      createdAtTo,
      page: String(page),
    },
  });

  if (batches.isPending || !batches.data) {
    return <Loading />;
  }

  return (
    <Table
      dataSource={batches.data.results}
      rowKey="id"
      columns={[
        {
          title: t('batch.type'),
          key: 'type',
          dataIndex: 'type',
          render: (value) => BatchTypeLabelModifier[value],
        },
        {
          title: t('batch.target'),
          key: 'target',
          dataIndex: 'target',
          render: (_, { target, type, params }) => {
            return convertBatchTarget(target, type, params);
          },
        },
        {
          title: t('batch.status'),
          key: 'status',
          dataIndex: 'status',
          width: 150,
          render: (_, record) =>
            convertBatchStatus({
              type: record.type,
              resultType: record.taskResult.result?.batchResult,
              status: record.status,
              processedCount: record.processedCount,
              targetCount: record.targetCount,
            }),
        },
        {
          title: t('batch.start-datetime'),
          key: 'dateCreated',
          dataIndex: 'dateCreated',
          width: TABLE_DATETIME_WIDTH,
          render: (_, record) =>
            datetimeFormat({ datetime: record.taskResult.dateCreated }),
        },
        {
          title: t('batch.end-datetime'),
          key: 'dateDone',
          dataIndex: 'dateDone',
          width: TABLE_DATETIME_WIDTH,
          render: (_, record) =>
            record.status === 'processing'
              ? FALLBACK_TEXT
              : datetimeFormat({ datetime: record.taskResult.dateDone }),
        },
        {
          title: t('batch.download-expires-at'),
          key: 'downloadExpiresAt',
          width: 120,
          render: (
            _,
            { downloadExpiresAt, hasDownloadFile, canDownloadFile },
          ) => {
            if (!hasDownloadFile || !downloadExpiresAt) {
              return FALLBACK_TEXT;
            }

            const date = dateFormat({ datetime: downloadExpiresAt });

            return canDownloadFile
              ? date
              : t('batch.download-termination-date', {
                  date,
                });
          },
        },
        {
          title: '',
          key: 'actions',
          width: TABLE_BUTTON_WIDTH,
          render: (_, record) => (
            <Button
              onClick={() => {
                navigate(`/batch/${record.id}`);
              }}
              type="primary"
            >
              {t('details')}
            </Button>
          ),
        },
      ]}
      pagination={{
        current: page,
        total: batches.data.count,
        showSizeChanger: false,
        pageSize: 10,
      }}
      onChange={(pagination, _, _sorter, extra) => {
        switch (extra.action) {
          case 'paginate':
            if (pagination.current) {
              setSearchQueryStates({ page: pagination.current });
            }
            break;
          default:
        }
      }}
    />
  );
};
